import React from "react";
import {
    Add,
    Download,
    FormatListBulleted,
    InfoOutlined,
    ModeEditOutlineTwoTone,
    NextPlan,
} from "@mui/icons-material";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import {
    useGetManifestoDetailsByLoadingIdsMutation,
    useLazyGetDDLShipmentLoadingListQuery,
    useLazyListShipmentLoadingQuery,
} from "store/services/shipmentLoadingApi";
import HighlightedText from "components/Shared/HighlightedText";
import { BaseButtonGroup } from "components/Shared/Base/BaseButtonGroup";
import { generateManifestoPrint } from "./ShipmentLoadingComponents/LoadingManifestoDataView";

// Custom component for rendering shipment IDs
const ShipmentIdCell = ({ row, keyword }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedShipments, setSelectedShipments] = React.useState([]);

    const shipments = Array.isArray(row.shipment) ? row.shipment : [];
    const visibleShipments = shipments.slice(0, 2);
    const remainingCount = shipments.length - visibleShipments.length;

    const handleChipClick = (event, allShipments) => {
        setAnchorEl(event.currentTarget);
        setSelectedShipments(allShipments);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return shipments.length > 0 ? (
        <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                {visibleShipments.map((shipment, index) => {
                    const shiId = shipment?.shipmentId
                        ?.replace(/(.{4})/g, "$1-")
                        .slice(0, -1);
                    return (
                        <span key={index} style={{ fontSize: "0.9rem" }}>
                            <HighlightedText
                                text={shipment.IsReturning ? `${shiId} (R)` : shiId}
                                highlight={keyword}
                            />
                            {index !== visibleShipments.length - 1 && ","}
                        </span>
                    );
                })}
                {remainingCount > 0 && (
                    <Tooltip title="Click to view all Shipment IDs">
                        <Chip
                            label={`+${remainingCount}`}
                            size="small"
                            sx={{
                                marginLeft: "4px",
                                height: "18px",
                                fontSize: "10px",
                                backgroundColor: "#e0e0e0",
                                color: "#333",
                                cursor: "pointer",
                            }}
                            onClick={(e) => handleChipClick(e, shipments)}
                        />
                    </Tooltip>
                )}
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box p={2} minWidth={100}>
                    {selectedShipments.map((shipment, index) => (
                        <Typography
                            key={index}
                            variant="body2"
                            sx={{ wordBreak: "break-word" }}
                        >
                            <HighlightedText
                                text={
                                    shipment.IsReturning
                                        ? `${shipment.shipmentId} (R)`
                                        : shipment.shipmentId
                                }
                                highlight={keyword}
                            />
                        </Typography>
                    ))}
                </Box>
            </Popover>
        </>
    ) : (
        "N/A"
    );
};

const DriverShipmentLoadingListScreen = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const { isDriver, loggedInUser } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate(); // Add useNavigate
    const keyword = searchParams.get("search") || "";

    const breadcrumbs = [{ title: "Shipment Loading" }];

    const columns = [
        {
            name: "ID",
            selector: (row) => row?.loadingId,
            cell: (row) => {
                const displayId = row?.loadingId;
                const path = !isDriver
                    ? `/app/crm/shipment-loading/${row?._id}/detail`
                    : `/app/driver/loading/${row?._id}/detail`;

                return displayId ? (
                    <Link
                        title={displayId}
                        to={path}
                        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                    >
                        <HighlightedText text={displayId} highlight={keyword} />
                    </Link>
                ) : (
                    "N/A"
                );
            },
        },
        {
            name: "Shipment ID",
            selector: (row) => row.shipment,
            cell: (row) => <ShipmentIdCell row={row} keyword={keyword} />,
        },
        {
            name: "UnLoading Status",
            selector: (row) => row?.unloadingStatus,
            cell: (row) => (row?.unloadingStatus) || "N/A",
        },

        {
            name: "Loading Time",
            selector: (row) => <AppTableDateTime date={row?.loadingTime} />,
        },
        {
            name: "Created At",
            selector: (row) => <AppTableDateTime date={row?.createdAt} />,
        },
        {
            name: "Total Shipment Weight",
            selector: (row) => (row.totalWeight ? `${row.totalWeight} kg` : "N/A"),
        },
        {
            name: "Actions",
            cell: (row) => {
                const loadedShipments = (row.shipment || []).filter(
                    (shipment) => shipment.status === "Loaded"
                );

                const isCreateUnloadingDisabled = loadedShipments.length === 0;

                return (
                    <Box display="flex" alignItems="center">
                        <Link
                            to={
                                !isDriver
                                    ? `/app/crm/shipment-loading/${row?._id}/detail`
                                    : `/app/driver/loading/${row?._id}/detail`
                            }
                        >
                            <IconButton
                                size="small"
                                aria-label="View Shipment Loading"
                                color="primary"
                            >
                                <InfoOutlined />
                            </IconButton>
                        </Link>

                        {!isDriver && (
                            <Link to={`/app/crm/shipment-loading/${row?._id}/edit`}>
                                <IconButton
                                    size="small"
                                    aria-label="Edit Shipment Loading"
                                >
                                    <ModeEditOutlineTwoTone />
                                </IconButton>
                            </Link>
                        )}

                        {!isDriver && (
                            <Tooltip
                                title={
                                    isCreateUnloadingDisabled
                                        ? "No Loaded shipments available"
                                        : "Create Shipment Unloading"
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Create Shipment Unloading"
                                        onClick={() => {
                                            navigate("/app/crm/shipment-unloading/create", {
                                                state: {
                                                    loading: {
                                                        _id: row._id,
                                                        loadingId: row.loadingId,
                                                        vehicle_id: row.vehicle_id,
                                                    },
                                                    shipments: loadedShipments,
                                                },
                                            });
                                        }}
                                        disabled={isCreateUnloadingDisabled}
                                        color="primary"
                                    >
                                        <NextPlan />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [
        fetchShipmentLoading,
        { data: shipmentLoadingRes, isLoading, isFetching, error },
    ] = useLazyListShipmentLoadingQuery();

    React.useEffect(() => {
        fetchShipmentLoading({
            page: currentPage,
            driverId: loggedInUser.typeRef,
            limit: perPage,
            ...(keyword && { keyword }),
        });
    }, [
        currentPage,
        fetchShipmentLoading,
        loggedInUser,
        keyword,
        perPage,
    ]);

    useAppMeta(`Shipment Loading`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <TableFilter
                    searchLabel="Loading ID / Shipment ID"
                    disabled={isLoading || isFetching}
                />
                <AppTable
                    columns={columns}
                    data={shipmentLoadingRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={shipmentLoadingRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default DriverShipmentLoadingListScreen;

const PageActions = () => {
    const { isDriver, viewEntity } = useAuth();
    const navigate = useNavigate();
    const [selectedLoadingIds, setSelectedLoadingIds] = React.useState([]);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [openDialog, setOpenDialog] = React.useState(false);

    const _loggedInAccount = viewEntity?.entity?.account?._id;
    const _loggedInEntity = viewEntity?.entity?._id;
    const isVendor = viewEntity?.entity?.typeRefSource === "Vendor";
    const isCustomer = viewEntity?.entity?.typeRefSource === "Customer";

    const [
        fetchDDLLoadings,
        { data: ddlLoadings, isLoading: isDDLLoad, isFetching: isDDLFetching },
    ] = useLazyGetDDLShipmentLoadingListQuery();
    const [getManifestoDetails, { isLoading: isManifestoLoading }] =
        useGetManifestoDetailsByLoadingIdsMutation();

    const handleGenerateManifestoClick = () => {
        setOpenDialog(true);
        setErrorMessage("");
        fetchDDLLoadings({ account: _loggedInAccount, entity: _loggedInEntity });
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setSelectedLoadingIds([]);
        setErrorMessage("");
    };

    const handleGenerateManifesto = async () => {
        if (selectedLoadingIds.length === 0) {
            setErrorMessage("Please select at least one Loading to print.");
            return;
        }

        try {
            const response = await getManifestoDetails(selectedLoadingIds).unwrap();
            setOpenDialog(false);
            setSelectedLoadingIds([]);
            setErrorMessage("");
            generateManifestoPrint(response, { isVendor, isCustomer });
        } catch (error) {
            setErrorMessage(
                error?.data?.message || "Error fetching manifesto details."
            );
        }
    };

    const handleGroupedActions = async (actionIndex) => {
        switch (actionIndex) {
            case 0:
                navigate("/app/crm/freeloading/create");
                break;
            case 1:
                navigate("/app/crm/freeloading/list");
                break;
            default:
                break;
        }
    };

    const ACTIONS = [
        {
            label: "Add Free Loading",
            icon: <Add fontSize="small" />,
            variant: "contained",
        },
        {
            label: "List Free Loading",
            icon: <FormatListBulleted fontSize="small" />,
            variant: "contained",
        },
    ];

    return (
        <>
            {!isDriver && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                    <BaseButtonGroup
                        actions={ACTIONS}
                        handleSelectedAction={handleGroupedActions}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleGenerateManifestoClick}
                    >
                        Generate Manifesto
                    </Button>
                    <Link to="/app/crm/shipment-loading/create">
                        <Button variant="contained" startIcon={<Add />}>
                            Shipment Loading
                        </Button>
                    </Link>
                </Box>
            )}
            {isDriver && (
                <div className="flex space-x-4">
                    {/* <Link to="/app/driver/transit-loading/create">
                        <Button variant="contained" size="small" startIcon={<Add />}>
                            Transit Loading
                        </Button>
                    </Link> */}
                    <Link to="/app/driver/destination-loading/create">
                        <Button variant="contained" size="small" startIcon={<Add />}>
                            Free Loading
                        </Button>
                    </Link>
                    <Link to="/app/driver/loading/create">
                        <Button variant="contained" size="small" startIcon={<Add />}>
                            Shipment Loading
                        </Button>
                    </Link>
                </div>
            )}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                sx={{ "& .MuiDialog-paper": { borderRadius: 2, p: 1 } }}
            >
                <DialogTitle
                    sx={{ bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
                >
                    Select Loading IDs
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {errorMessage && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setErrorMessage("")}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                    <Autocomplete
                        multiple
                        fullWidth
                        options={ddlLoadings || []}
                        getOptionLabel={(option) => option.loadingId || ""}
                        value={
                            ddlLoadings?.filter((loading) =>
                                selectedLoadingIds.includes(loading.loadingId)
                            ) || []
                        }
                        onChange={(event, newValue) => {
                            setSelectedLoadingIds(
                                newValue.map((loading) => loading.loadingId)
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Loading IDs"
                                placeholder="Search Shipment Loading..."
                                helperText={
                                    isDDLLoad || isDDLFetching
                                        ? "Loading..."
                                        : "Select one or more Shipment Loading IDs"
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 1,
                                        "&:hover fieldset": { borderColor: "#3b82f6" },
                                        "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                                    },
                                }}
                            />
                        )}
                        disabled={isDDLLoad || isDDLFetching}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions
                    sx={{ bgcolor: "#f9fafb", borderTop: "1px solid #e5e7eb", p: 2 }}
                >
                    <Button
                        onClick={handleDialogClose}
                        sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateManifesto}
                        disabled={isManifestoLoading}
                        sx={{
                            bgcolor: "#3b82f6",
                            "&:hover": { bgcolor: "#2563eb" },
                            "&:disabled": { bgcolor: "#9ca3af" },
                        }}
                    >
                        {isManifestoLoading ? "Loading..." : "Generate Manifesto"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};